import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChefHat } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { BRAND } from "@/lib/brand";
import { resolveImageUrl } from "@/lib/api";

const LAYOUTS = [
  { key: "gala", image: BRAND.layoutPhotos.gala },
  { key: "cocktail", image: BRAND.layoutPhotos.cocktail },
  { key: "meeting", image: BRAND.layoutPhotos.meeting },
];

export default function LayoutConfigurations() {
  const { t } = useI18n();
  const [active, setActive] = useState(0);
  const items = t.layouts.items;

  return (
    <section
      id="configuraciones"
      data-testid="layouts-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F2EDE5] aura-grain"
    >
      <div className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl mb-12"
        >
          <p className="uppercase tracking-[0.28em] text-xs text-[#3e664c] mb-4">{t.layouts.eyebrow}</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            {t.layouts.title}
          </h2>
          <p className="mt-5 text-[#5C544F] leading-relaxed">{t.layouts.desc}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-stretch">
          {/* Big image */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-3 relative rounded-3xl overflow-hidden min-h-[420px] md:min-h-[520px] shadow-[0_25px_50px_rgb(42,36,33,0.18)]"
            data-testid="layouts-stage"
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={LAYOUTS[active].key}
                src={resolveImageUrl(LAYOUTS[active].image)}
                alt={items[active]?.name}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-[#1c3327]/70 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-[#fdfcf9]">
              <p className="uppercase tracking-[0.22em] text-[10px] opacity-80 mb-1">{`0${active + 1}`}</p>
              <p className="font-display text-3xl md:text-4xl tracking-tight" data-testid="layout-stage-title">
                {items[active]?.name}
              </p>
            </div>
          </motion.div>

          {/* Selector */}
          <div className="lg:col-span-2 flex flex-col gap-3">
            {items.map((it, i) => (
              <button
                key={it.name}
                onClick={() => setActive(i)}
                data-testid={`layout-option-${i}`}
                className={`text-left p-5 rounded-2xl border transition-all ${
                  active === i
                    ? "bg-[#3e664c] text-[#fdfcf9] border-[#3e664c] shadow-[0_18px_35px_rgb(44,72,60,0.22)]"
                    : "bg-white text-[#2A2421] border-[#eae0df] hover:border-[#3e664c]"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-[0.22em] opacity-70">{`0${i + 1}`}</span>
                  <span className={`w-1.5 h-1.5 rounded-full ${active === i ? "bg-[#E2C9A0]" : "bg-[#3e664c]"}`} />
                </div>
                <p className="font-display text-2xl tracking-tight leading-tight">{it.name}</p>
                <p className={`text-sm mt-2 leading-relaxed ${active === i ? "text-[#fdfcf9]/80" : "text-[#5C544F]"}`}>
                  {it.desc}
                </p>
              </button>
            ))}

            {/* Catering highlight */}
            <div
              className="mt-2 p-6 rounded-2xl bg-gradient-to-br from-[#3e664c] to-[#1c3327] text-[#fdfcf9] relative overflow-hidden"
              data-testid="catering-highlight"
            >
              <ChefHat size={28} className="text-[#E2C9A0] mb-3" />
              <p className="uppercase tracking-[0.22em] text-[10px] text-[#E2C9A0] mb-2">
                {t.layouts.catering.eyebrow}
              </p>
              <p className="font-display text-2xl tracking-tight leading-tight">{t.layouts.catering.title}</p>
              <p className="text-sm mt-3 text-[#fdfcf9]/80 leading-relaxed">{t.layouts.catering.desc}</p>
              <a
                href="#contacto"
                data-testid="layouts-cta-calendar"
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#E2C9A0] hover:bg-[#fdfcf9] text-[#2e5949] text-xs uppercase tracking-[0.18em] transition-colors"
              >
                {t.cta.bookDate}
              </a>
            </div>
          </div>
        </div>

        {/* Custom experience CTAs */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="#contacto"
            data-testid="layouts-cta-custom"
            className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#3e664c] hover:bg-[#2e5949] text-[#fdfcf9] text-sm uppercase tracking-[0.18em] font-medium transition-colors text-center"
          >
            {t.cta.customExperience}
          </a>
          <a
            href="#disponibilidad"
            data-testid="layouts-cta-own"
            className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-[#3e664c]/30 hover:border-[#3e664c] text-[#3e664c] text-sm uppercase tracking-[0.18em] font-medium transition-colors"
          >
            {t.cta.bookOwn}
          </a>
        </div>
      </div>
    </section>
  );
}
